/**
 * PostMinutesModal — the confirm step for "Post & email minutes".
 *
 * Before anything is issued, this shows exactly what will go out: the email subject + body, the
 * resolved recipient list (so we can confirm everyone has an address on file), and an optional CC
 * field for FYI copies. Posting is irreversible-ish (it locks the meeting), so we make the recipients
 * explicit rather than firing a one-line confirm. The preview content is fetched from the server
 * (api/meeting-minutes previewEmail) so it matches the sent email byte-for-byte.
 */

import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ctx } from '../outline/ctx.js'
import { wf } from '../outline/state.js'
import { previewMeetingEmail, postMeetingMinutes, addMinutesToClientHub, getMeetingNotesDraft } from '../services/meetings.js'
import { alertDialog, confirmDialog } from './Dialog.jsx'

const getToken = () => Promise.resolve((typeof window !== 'undefined' && window.__clerkGetToken) ? window.__clerkGetToken() : null)
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function PostMinutesModal({ meetingId, projectId, projectName, onClose, onPosted }) {
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [preview, setPreview] = useState(null)   // { subject, lines, attendees:[{name,email}], emails, ... }
  const [cc, setCc] = useState([])               // FYI copy addresses
  const [ccInput, setCcInput] = useState('')
  const [posting, setPosting] = useState(false)
  const [journeyChoice, setJourneyChoice] = useState('none')   // 'none' | 'draft' | 'final'
  // A Zoom notes import still open on this meeting (Brandon, 2026-10-29: posted while the notes
  // waited for approval, and the minutes went out blank). Its items aren't on threads yet, so
  // posting now would issue minutes without them. Committing the import posts the minutes itself.
  const [openImport, setOpenImport] = useState(null)   // draft row | null

  useEffect(() => {
    let alive = true
    getMeetingNotesDraft(meetingId, { getToken }).then(r => { if (alive) setOpenImport((r && r.draft) || null) }).catch(() => {})
    return () => { alive = false }
  }, [meetingId])
  const importWaiting = openImport
    ? 'The Zoom notes for this meeting are not committed yet (' + (openImport.status === 'ready_for_review'
        ? 'waiting for ' + (openImport.reviewer_name || 'review')
        : openImport.sent_back_by_member_id ? 'sent back to ' + (openImport.submitted_by_name || 'the drafter') + ' to fix' : 'still a draft')
      + '), so the minutes would post with none of the items discussed. Committing the import posts the minutes with them.'
    : ''

  useEffect(() => {
    let alive = true
    setLoading(true); setErr('')
    previewMeetingEmail(meetingId, { getToken }).then(res => {
      if (!alive) return
      if (!res || res.error) setErr((res && res.error) || 'Could not load the email preview.')
      else setPreview(res)
      setLoading(false)
    })
    return () => { alive = false }
  }, [meetingId])

  function addCc(raw) {
    const parts = String(raw).split(/[,;\s]+/).map(s => s.trim()).filter(Boolean)
    if (!parts.length) return
    setCc(prev => Array.from(new Set([...prev, ...parts])))
    setCcInput('')
  }
  function onCcKeyDown(e) {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') { e.preventDefault(); addCc(ccInput) }
    else if (e.key === 'Backspace' && !ccInput && cc.length) setCc(prev => prev.slice(0, -1))
  }
  function removeCc(addr) { setCc(prev => prev.filter(a => a !== addr)) }

  const withEmail = preview ? preview.attendees.filter(a => a.email) : []
  const missing = preview ? preview.attendees.filter(a => !a.email) : []
  const badCc = cc.filter(a => !EMAIL_RE.test(a))

  async function doPost(sendEmail) {
    if (openImport) { await alertDialog({ title: 'Notes not committed yet', message: importWaiting + ' Open the import from the meeting menu.' }); return }
    const cleanCc = cc.filter(a => EMAIL_RE.test(a))
    // Final catch against an accidental click — posting is irreversible (locks the meeting).
    const recips = sendEmail ? withEmail.length + cleanCc.length : 0
    const who = recips ? `email ${recips} recipient${recips !== 1 ? 's' : ''} and ` : ''
    if (!(await confirmDialog({
      title: sendEmail ? 'Post & email these minutes?' : 'Post without emailing?',
      message: sendEmail
        ? `This will ${who}lock the minutes as the issued document of record. This cannot be undone. Continue?`
        : 'This locks the minutes as the issued document of record, without emailing attendees — use this when delivery happens another way (e.g. the Client Hub). This cannot be undone. Continue?',
      confirmLabel: sendEmail ? 'Post & email' : 'Post without email',
    }))) return
    setPosting(true)
    const postingMemberId = (ctx.currentMember && ctx.currentMember.id) || wf.currentMemberId || null
    const res = await postMeetingMinutes(meetingId, { send: sendEmail, getToken, postingMemberId, cc: sendEmail ? cleanCc : [] })
    setPosting(false)
    if (res && res.error) { await alertDialog({ title: 'Post failed', message: res.error }); return }

    // Optionally add this freshly-posted meeting's minutes to the Client Hub (draft or final) —
    // best-effort, never blocks or reverses the post itself (which already succeeded above).
    let journeyNote = null
    if (journeyChoice !== 'none') {
      try {
        await addMinutesToClientHub({
          meetingId, projectId, projectName, published: journeyChoice === 'final', post: res,
          postedBy: { memberId: postingMemberId, name: (ctx.currentMember && ctx.currentMember.fields && (ctx.currentMember.fields['Name'] || ctx.currentMember.fields['Member Name'])) || null },
        })
      } catch (e) { journeyNote = { ok: false, error: e.message || String(e) } }
    }

    onPosted && onPosted(res)
    onClose && onClose()
    const ccMsg = res && res.ccd ? ` (CC: ${res.ccd})` : ''
    await alertDialog({
      title: 'Minutes posted',
      message: res && res.emailed ? `Posted and emailed to ${res.emailed} attendee${res.emailed !== 1 ? 's' : ''}${ccMsg}.` : 'Posted.',
    })
    if (journeyNote && !journeyNote.ok) {
      await alertDialog({
        title: 'Posted — but not added to the Client Hub',
        message: 'The minutes posted, but adding them to the Client Hub failed:\n\n' + journeyNote.error + '\n\nYou can add them from the Client Hub branch (⋯ → Add entry).',
      })
    }
  }

  return createPortal(
    <div style={overlay} onClick={onClose}>
      <div data-modal-id="PostMinutesModal" style={card} role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}>
        <div style={head}>
          <span style={title}>Post &amp; email minutes</span>
          <button onClick={onClose} style={xBtn}>×</button>
        </div>

        <div style={{ padding: '16px 18px', flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
          {loading && <div style={{ color: 'var(--aj-text-mid)', fontSize: 13 }}>Loading recipients…</div>}
          {err && <div style={{ color: '#c0392b', fontSize: 13 }}>{err}</div>}

          {openImport && <div style={warnBox}>{importWaiting} Open it from the meeting menu instead of posting here.</div>}
          {preview && (
            <>
              {/* Recipients */}
              <div style={sectionLabel}>Recipients ({withEmail.length})</div>
              <div style={listBox}>
                {withEmail.length === 0 && (
                  <div style={{ fontSize: 12.5, color: '#b8860b', fontStyle: 'italic' }}>
                    No attendees have an email on file — nothing will be sent.
                  </div>
                )}
                {withEmail.map(a => (
                  <div key={a.email} style={recipRow}>
                    <span style={{ color: 'var(--aj-text)', fontWeight: 600 }}>{a.name}</span>
                    <span style={{ color: 'var(--aj-text-mid)' }}>{a.email}</span>
                  </div>
                ))}
              </div>

              {missing.length > 0 && (
                <div style={warnBox}>
                  {missing.length} attendee{missing.length !== 1 ? 's have' : ' has'} no email on file and won't receive the minutes:
                  {' '}{missing.map(a => a.name).join(', ')}. Add their email in Contacts to include them.
                </div>
              )}

              {/* CC */}
              <div style={{ ...sectionLabel, marginTop: 14 }}>CC (FYI copies — optional)</div>
              <div style={ccWrap}>
                {cc.map(a => (
                  <span key={a} style={{ ...chip, ...(EMAIL_RE.test(a) ? null : chipBad) }}>
                    {a}
                    <button type="button" onClick={() => removeCc(a)} style={chipX}>×</button>
                  </span>
                ))}
                <input
                  value={ccInput}
                  onChange={e => setCcInput(e.target.value)}
                  onKeyDown={onCcKeyDown}
                  onBlur={() => ccInput && addCc(ccInput)}
                  placeholder={cc.length ? '' : 'name@example.com'}
                  style={ccInputStyle}
                />
              </div>
              {badCc.length > 0 && (
                <div style={{ fontSize: 11.5, color: '#c0392b', marginTop: 4 }}>
                  Not a valid email: {badCc.join(', ')} — these will be skipped.
                </div>
              )}

              {/* Email preview */}
              <div style={{ ...sectionLabel, marginTop: 14 }}>Email preview</div>
              <div style={emailBox}>
                {preview.from && (
                  <div style={{ fontSize: 12.5, color: 'var(--aj-text)', marginBottom: 4 }}>
                    <span style={{ color: 'var(--aj-text-muted)' }}>From: </span>{preview.from}
                  </div>
                )}
                {preview.replyTo && (
                  <div style={{ fontSize: 11.5, color: 'var(--aj-text-muted)', marginBottom: 8 }}>
                    Replies go to {preview.replyTo}
                  </div>
                )}
                <div style={{ fontSize: 12.5, color: 'var(--aj-text)', marginBottom: 8 }}>
                  <span style={{ color: 'var(--aj-text-muted)' }}>Subject: </span>{preview.subject}
                </div>
                {preview.lines.map((l, i) => (
                  <p key={i} style={{ margin: '0 0 8px', fontSize: 13, lineHeight: 1.5, color: 'var(--aj-text)' }}>{l}</p>
                ))}
                <div style={attachRow}>
                  <span style={{ fontSize: 16 }}>📄</span>
                  <span>{preview.attachmentName || 'Meeting minutes.pdf'}</span>
                </div>
              </div>

              <div style={{ fontSize: 11.5, color: 'var(--aj-text-muted)', marginTop: 12 }}>
                Once posted, the minutes are locked and the issued PDF becomes the document of record.
              </div>

              {/* Client Hub — every meeting gets the choice; "Don't add" is the safe default. */}
              <div style={{ ...sectionLabel, marginTop: 14 }}>Client Hub</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[
                  { v: 'none', t: 'Don’t add', d: 'Post only — nothing goes to the Client Hub.' },
                  { v: 'draft', t: 'Add as draft', d: 'Saved to the Client Hub unpublished. Your team can preview it; the client can’t see it yet.' },
                  { v: 'final', t: 'Add as final', d: 'Published immediately — visible to the client now, sorted by date under its phase.' },
                ].map(opt => {
                  const active = journeyChoice === opt.v
                  return (
                    <label key={opt.v} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 9, cursor: 'pointer',
                      padding: '9px 11px', borderRadius: 6,
                      border: `0.5px solid ${active ? '#1D9E75' : 'var(--aj-border)'}`,
                      background: active ? 'rgba(29,158,117,0.06)' : 'transparent',
                    }}>
                      <input type="radio" name="journeyChoice" checked={active}
                        onChange={() => setJourneyChoice(opt.v)}
                        style={{ marginTop: 2, flexShrink: 0, accentColor: '#1D9E75', cursor: 'pointer' }} />
                      <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', lineHeight: 1.4 }}>
                        {opt.t}
                        <div style={{ fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 2 }}>{opt.d}</div>
                      </div>
                    </label>
                  )
                })}
              </div>
            </>
          )}
        </div>

        <div style={foot}>
          <button onClick={onClose} style={ghostBtn} disabled={posting}>Cancel</button>
          <button onClick={() => doPost(false)} style={ghostBtn} disabled={loading || posting || !!err || !!openImport} title="Post without emailing attendees — use when delivery happens another way (e.g. the Client Hub)">
            Post without email
          </button>
          <button onClick={() => doPost(true)} style={primaryBtn} disabled={loading || posting || !!err || !!openImport}>
            {posting ? 'Posting…' : 'Post & email'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 600, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 16px', overflowY: 'auto' }
const card = { width: '100%', maxWidth: 560, maxHeight: '90vh', display: 'flex', flexDirection: 'column', background: 'var(--aj-bg-elevated)', borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,0.25)' }
const head = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '0.5px solid var(--aj-border)', flexShrink: 0 }
const title = { fontFamily: 'Montserrat,sans-serif', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 13, fontWeight: 700, color: 'var(--aj-text)' }
const xBtn = { background: 'none', border: 'none', fontSize: 22, lineHeight: 1, color: 'var(--aj-text-muted)', cursor: 'pointer' }
const foot = { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 18px', borderTop: '0.5px solid var(--aj-border)', flexShrink: 0 }
const sectionLabel = { fontFamily: '"DM Mono",monospace', fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginBottom: 6 }
const listBox = { border: '0.5px solid var(--aj-border)', borderRadius: 8, padding: '8px 10px', background: 'var(--aj-bg-subtle)' }
const recipRow = { display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12.5, padding: '3px 0' }
const warnBox = { marginTop: 8, padding: '8px 10px', borderRadius: 6, background: 'rgba(184,134,11,0.08)', border: '0.5px solid rgba(184,134,11,0.3)', fontSize: 12, lineHeight: 1.45, color: '#b8540a' }
const ccWrap = { display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '6px 8px', background: 'var(--aj-bg-subtle)' }
const chip = { display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--aj-bg-muted)', borderRadius: 20, padding: '2px 6px 2px 10px', fontSize: 12, color: 'var(--aj-text)' }
const chipBad = { background: 'rgba(192,57,43,0.1)', color: '#c0392b' }
const chipX = { background: 'none', border: 'none', fontSize: 14, lineHeight: 1, color: 'inherit', cursor: 'pointer', padding: 0 }
const ccInputStyle = { flex: 1, minWidth: 140, border: 'none', outline: 'none', fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', background: 'transparent', padding: '2px 0' }
const emailBox = { border: '0.5px solid var(--aj-border)', borderRadius: 8, padding: '14px', background: 'var(--aj-bg-subtle)' }
const attachRow = { display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, padding: '8px 10px', borderRadius: 6, background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', fontSize: 12.5, color: 'var(--aj-text)' }
const primaryBtn = { background: '#07272D', border: 'none', borderRadius: 4, padding: '7px 16px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: '#fff' }
const ghostBtn = { background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '7px 16px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }
